/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { Fragment, useState, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import cx from 'classnames';
import Select from 'react-select';
import TruncatedText from 'comps/TruncatedText';
import intersect from 'lib/intersect';
import EmptyArray from 'lib/EmptyArray';
import GetDataPanel from './queries/GetDataPanel';
import GetDataPanels from './queries/GetDataPanels';
import GetDataPanelData from './queries/GetDataPanelData';
import GetTags from './queries/GetTags';
import Modal from './forms/Modal';
import CreateDataPanel from './mutations/CreateDataPanel';
import UpdateDataPanel from './mutations/UpdateDataPanel';
import refetchQueries from './queries/refetchQueries';
import { schema } from './forms/dataPanel';
import lensPath from 'ramda/src/lensPath';
import setPath from 'ramda/src/set';
import nested from 'lib/nested';
import { NonEmptyArray } from 'types/tcomb';
import DestroyDataPanel from './mutations/DestroyDataPanel';
import { useMutation } from '@apollo/react-hooks';
import useCommands from './useCommands';
import useContextState from './useContextState';
import useQueryParams from './useQueryParams';
import useCompanyId from './useCompanyId';
import useBbox from './useBbox';
import OverlayTriggerWrapper from 'comps/OverlayTriggerWrapper';
import { Tooltip, Dropdown, Popover } from 'react-bootstrap';
import useCachedQuery from 'lib/useCachedQuery';
import * as I from 'immutable';
import ErrorsView from 'comps/ErrorsView';
import Present from 'lib/Present';
import Blank from 'lib/Blank';
import isEmbedded from 'lib/isEmbedded';
import same from 'lib/same';
import compose from 'lib/compose';
import truncate from 'underscore.string/truncate';
import isAdmin from 'lib/isAdmin';

function LTPublicDataSearchInputClearBtn() {
  const cmd = useCommands();
  const onClick = () => {
    cmd.set(['locPubSearchQuery'], '');
  };
  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={<Tooltip id="clear-btn-tooltip">Clear</Tooltip>}
    >
      <a id="clear-btn" onClick={onClick}>
        <i className="fa fa-times" />
      </a>
    </OverlayTriggerWrapper>
  );
}

function useClearDataPanel() {
  const cmd = useCommands();

  const onClear = () => {
    cmd.set(['locActiveId'], null);
    cmd.set(['locActiveName'], null);
  };

  return onClear;
}

function LTPublicDataSearchInput() {
  const cmd = useCommands();
  const { locPubSearchQuery: searchText, locActiveId } = useContextState();
  const isSearching = Boolean(locActiveId) === false;
  const placeholder = isSearching ? 'Start typing...' : 'Search datasets';
  const onFocus = useClearDataPanel();
  if (!isSearching) return null;

  function SearchIcon() {
    return <i className="fa fa-search" />;
  }
  const Icon = searchText ? LTPublicDataSearchInputClearBtn : SearchIcon;

  return (
    <div data-testid="LTPublicDataSearchInput" className="pr-4 pl-3 pt-3 mb-4">
      <div
        className="input-group pt-2 pl-3 pr-2 pb-1"
        css={css(`
        border: solid 2px var(--teal);
      `)}
      >
        <input
          type="text"
          className="form-control border-0 pl-0"
          placeholder={placeholder}
          onFocus={onFocus}
          value={searchText}
          onChange={(e) => {
            cmd.set(['locPubSearchQuery'], e.target.value);
          }}
        />
        <span className="input-group-prepend">
          <span className="input-group-text bg-transparent">
            <Icon />
          </span>
        </span>
      </div>
    </div>
  );
}

function LTPublicDataSRIHeader({ name }) {
  return (
    <h4
      data-testid="LTPublicDataSRIHeader"
      css={css('color: #333;')}
      className="d-flex justify-content-between mb-3"
    >
      <span className="text-uppercase">{name}</span>
      <span>
        <i className="fa fa-caret-right" />
      </span>
    </h4>
  );
}

function LTPublicDataSRIBody({ description }) {
  return (
    <div
      data-testid="LTPublicDataSRIBody"
      css={css('color: #777;')}
      className="mb-4"
    >
      <TruncatedText value={description} limit={100} />
    </div>
  );
}

function LTPublicDataSRIFooterTags({ tags }) {
  return tags.map((tag, i) => (
    <div
      className="mr-2"
      css={css(`
      color: #333;
      &:not(:last-child)::after {
        content: ',';
      }
     `)}
      key={i}
    >
      {tag}
    </div>
  ));
}

function LTPublicDataSRIFooter({ source, tags }) {
  return (
    <div
      data-testid="LTPublicDataSRIFooter"
      css={css('color: #999;font-size: 1.2rem;')}
    >
      <div className="mb-2">
        <span>Source:</span>{' '}
        <span
          css={css('color: #333;')}
          dangerouslySetInnerHTML={{ __html: source || 'Ginkgo' }}
        />
      </div>
      <div className="d-flex align-items-center">
        <i className="fa fa-tags mr-2" />{' '}
        <LTPublicDataSRIFooterTags tags={tags} />
      </div>
    </div>
  );
}

function useDataPanelTags() {
  const companyId = useCompanyId();
  const opts = { variables: { companyId } };
  const o = useCachedQuery(GetTags, opts);
  const { data, ...rest } = o;
  const tags = I.getIn(data, ['company', 'dataPanelTags'], []);
  return { data: tags, ...rest };
}

function LTPublicDataSRTags() {
  const cmd = useCommands();
  const { data: tags, errors } = useDataPanelTags();
  const options = tags.map((v) => ({ value: v, label: v }));
  const value = options.find((v) => value === v.value);
  const onChange = (opt) => {
    // Update the tag in context state.
    cmd.set(['locPubSearchTag'], opt ? opt.value : '');
  };

  return (
    <Fragment>
      <ErrorsView errors={errors} />
      <div
        data-testid="LTPublicDataSRTags"
        className="d-flex border-top border-bottom pl-3 pr-3 pt-2 pb-2 align-items-center"
        css={css(`
          border-color: #efefef;
          color: #999;
      `)}
      >
        <div css={css('width: 40px;')}>Tags:</div>
        <div className="w-100">
          <Select
            isMulti={false}
            onChange={onChange}
            styles={{
              container: (base) => ({ ...base, display: 'inline-block' }),
              input: (base) => ({ ...base, border: 'none' }),
              menu: (base) => ({ ...base, width: 300 }),
              control: (base) => ({
                ...base,
                border: 'none',
                borderRadius: 0,
                width: 200,
                boxShadow: 'none',
              }),
            }}
            cacheOptions
            defaultOptions
            isClearable
            value={value}
            placeholder="Select..."
            options={options}
            name="tags"
            classNamePrefix="Select"
          />
        </div>
      </div>
    </Fragment>
  );
}

function LTPublicDataSRSummary({ length }) {
  if (!length) return null;
  return (
    <div
      data-testid="LTPublicDataSRSummary"
      className="d-flex border-bottom pl-3 pr-3 pt-2 pb-2 align-items-center"
      css={css(`
        border-color: #efefef;
        color: #999;
      `)}
    >
      Showing {length} results
    </div>
  );
}

function LTPublicDataSRItem({ item }) {
  const cmd = useCommands();
  const { id, name } = item;
  function onClick() {
    cmd.set(['locActiveId'], id);
    cmd.set(['locActiveName'], name);
  }

  return (
    <div
      onClick={onClick}
      data-testid="LTPublicDataSRItem"
      className="p-3 border-bottom cursor-pointer"
      css={css(`
        &:hover {
          background: #efefef;
        }
       `)}
    >
      <LTPublicDataSRIHeader name={name} id={id} />
      <LTPublicDataSRIBody description={item.description} />
      <LTPublicDataSRIFooter source={item.source} tags={item.tags} />
    </div>
  );
}

const filterKeyByRegExTest = (key, value) => (v) => {
  return !value || value === '' || new RegExp(value, 'gi').test(v[key]);
};

const filterByKeyAndRegEx = (key, value) => (coll) => {
  return coll.filter(filterKeyByRegExTest(key, value));
};

const filterByTags = (tags) => (coll) => {
  const ts = tags.filter(Boolean);
  const byIntersect = (v) => {
    return Present.is(v.tags) && Present.is(intersect(v.tags, ts));
  };
  return EmptyArray.is(ts) ? coll : coll.filter(byIntersect);
};

function useDataPanels() {
  let ids = useQueryParams().getIn(['load', 'dataPanels']);
  ids = Blank.is(ids) && isEmbedded() ? [1] : ids;
  const bbox = useBbox();
  const variables = { bbox, ids };
  const opts = { variables };
  const o = useCachedQuery(GetDataPanels, opts);
  const { data, ...rest } = o;
  const d = I.getIn(data, ['dataPanels'], []);
  return { data: d, ...rest };
}

function LTPublicDataSRNoResults() {
  const text = 'No data panels for this query.';
  return (
    <div
      data-testid="LTPublicDataSRNoResults"
      className="d-flex justify-content-center align-items-center"
      css={css('min-height: 200px;')}
    >
      {text}
    </div>
  );
}

function useCurrentDataPanel() {
  const { locActiveId: id } = useContextState();
  const { data, ...rest } = useDataPanels();
  const item = data.find((v) => v.id === id);
  return { ...rest, data: item };
}

// Abbrev: LTPublicDataSR
function LTPublicDataSearchResults() {
  const {
    locPubSearchQuery: searchText,
    locPubSearchTag: tag,
  } = useContextState();
  const { data, loading, error, errors } = useDataPanels();

  const filter = compose(
    filterByKeyAndRegEx('name', searchText),
    filterByTags([tag])
  );

  const items = filter(data);

  return (
    <div data-testid="LTPublicDataSR">
      <LTPublicDataSRTags />
      <ErrorsView errors={errors} error={error} />
      <LTPublicDataSRSummary length={items.length} />
      {!items.length && (tag || searchText) && <LTPublicDataSRNoResults />}
      {loading && <LTPublicDataSRSkeleton />}
      {!loading &&
        items.map((item, i) => <LTPublicDataSRItem key={i} item={item} />)}
    </div>
  );
}

function LTPublicDataSRSkeletonItem() {
  return (
    <div
      data-testid="LTPublicDataSRSkeletonItem"
      className="p-3"
      css={css(`
      div {
        background-color: #efefef;
      }
    `)}
    >
      <div css={css('height: 2rem; width: 100%;')} className="mb-3" />
      <div css={css('height: 1rem; width: 100%;')} className="mb-1" />
      <div css={css('height: 1rem; width: 100%;')} className="mb-4" />
      <div css={css('height: 1.2rem; width: 100%;')} className="mb-1" />
      <div css={css('height: 1.2rem; width: 100%;')} className="mb-1" />
      <div css={css('height: 1.2rem; width: 100%;')} className="mb-3" />
      <div css={css('height: 1.2rem; width: 20%;')} className="bg-light mb-1" />
    </div>
  );
}

function LTPublicDataSRSkeleton() {
  return Array(3)
    .fill(null)
    .map((_, i) => <LTPublicDataSRSkeletonItem key={i} />);
}

function LTPDSIDEmpty() {
  const cmd = useCommands();
  const onClick = () => {
    cmd.set(['locActiveId'], null);
    cmd.set(['locActiveName'], null);
  };
  return (
    <div
      data-testid="LTPDSIDEmpty"
      className="d-flex align-items-center justify-content-center"
      css={css('height: 200px;')}
    >
      <p>
        Please select a{' '}
        <button
          className="btn btn-link p-0"
          css={css('text-transform: initial; font-size: 1.4rem;')}
          onClick={onClick}
        >
          data panel.
        </button>{' '}
      </p>
    </div>
  );
}

function LTPDSIDContentHeaderEditBtn() {
  const cmd = useCommands();
  const onClick = () => {
    cmd.set(['locActiveModalName'], 'EDIT_DATA_PANEL');
  };

  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={<Tooltip id={'edit-btn-tooltip'}>Edit panel</Tooltip>}
    >
      <Dropdown.Item
        eventKey="1"
        onClick={onClick}
        className="pr-3 pt-2 pb-2 text-right pl-2"
        data-testid="LTPDSIDContentHeaderEditBtn"
      >
        Edit <i className="fa fa-edit" />
      </Dropdown.Item>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentHeaderDuplicateBtn() {
  const cmd = useCommands();
  const onClick = () => {
    cmd.set(['locActiveModalName'], 'DUP_DATA_PANEL');
  };

  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={<Tooltip id={'edit-btn-tooltip'}>Duplicate panel</Tooltip>}
    >
      <Dropdown.Item
        eventKey="1"
        onClick={onClick}
        className="pr-3 pt-2 pb-2 text-right pl-2"
        data-testid="LTPDSIDContentHeaderDuplicateBtn"
      >
        Duplicate <i className="fa fa-copy" />
      </Dropdown.Item>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentHeaderSettingsDropdownButton() {
  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={<Tooltip id="data-panel-settings-tooltip">Settings</Tooltip>}
    >
      <Dropdown.Toggle
        variant=""
        id={'dropdown-config-btn'}
        className="p-0 mr-3"
        data-testid="LTPDSIDContentHeaderSettingsDropdownButton"
      >
        <i className="fa fa-cog" />
      </Dropdown.Toggle>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentHeaderSettings() {
  if (!isAdmin()) return null;
  return (
    <Dropdown
      className="config-btn"
      id={'dropdown-config-btn'}
      css={css('.dropdown-item {color: #212529 !important;}')}
      data-testid="LTPDSIDContentHeaderSettings"
    >
      <LTPDSIDContentHeaderSettingsDropdownButton />
      <Dropdown.Menu>
        <LTPDSIDContentHeaderEditBtn />
        <LTPDSIDContentHeaderDuplicateBtn />
      </Dropdown.Menu>
    </Dropdown>
  );
}

function LTPDSIDToggleMapLayerButton() {
  const cmd = useCommands();
  const { layersActiveLayers } = useContextState();
  const { data: panel } = useCurrentDataPanel();
  const layerUuid = panel ? panel.layerUuid : '';
  const isActive = layersActiveLayers.has(layerUuid);

  const onClick = () => {
    cmd.layerToggle(layerUuid);
  };

  const isDisabled = !layerUuid;

  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={
        <Tooltip id="data-panel-settings-tooltip">Toggle map layer</Tooltip>
      }
    >
      <button
        className="p-0 text-orange"
        onClick={onClick}
        disabled={isDisabled}
        data-testid="LTPDSIDToggleMapLayerButton"
      >
        <i
          className={cx('fa', {
            'fa-toggle-off': !isActive,
            'fa-toggle-on': isActive,
          })}
        />
      </button>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentHeader() {
  const { locActiveName: name } = useContextState();

  return (
    <div
      data-testid="LTPDSIDContentHeader"
      className="border-top border-bottom"
      css={css(`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 1.5rem 0.5rem 1rem;
      `)}
    >
      <h4 className="text-uppercase m-0 text-muted">{truncate(name, 33)}</h4>
      <div className="d-flex">
        <LTPDSIDContentHeaderSettings />
        <LTPDSIDToggleMapLayerButton />
      </div>
    </div>
  );
}

function LTPDSIDContentBodyRow({ label, value }) {
  return (
    <div
      css={css(`
        display: grid;
        grid-template-columns: 1fr 1fr;
        padding: 2px 15px 4px 10px;
        border-bottom: 1px solid rgb(239, 239, 239);
      `)}
    >
      <div className="font-weight-bold">{label}</div>
      <div className="text-right" dangerouslySetInnerHTML={{ __html: value }} />
    </div>
  );
}

function LTPDSIDContentBodySkeleton() {
  const Row = () => (
    <div css={css('height: 2rem; background: #efefef;')} className="mb-3" />
  );

  return (
    <div
      data-testid="LTPDSIDContentBodySkeleton"
      className="pt-4 pl-4 pr-4 pb-2"
    >
      <Row />
      <Row />
      <Row />
      <Row />
      <Row />
      <Row />
    </div>
  );
}

function createBag(arr) {
  return arr.map((v) => Object.values(v).join('|'));
}

const testItem = (item, text) => new RegExp(text, 'gi').test(item);

function filterBag(arr, text) {
  let bag = [];
  arr.forEach((item, i) => testItem(item, text) && bag.push(i));
  return bag;
}

function LTPDSIDContentBodySearch(props: {
  results: any[],
  searchText: string,
  setSearchResults: Function,
  setSearchText: Function,
}) {
  const { results, searchText, setSearchResults, setSearchText } = props;
  const s = useContextState();
  const name = s.locActiveName || '';

  const [wordBag] = useState(() => createBag(results));
  if (results.length < 2) return null;

  function onSearch(value) {
    setSearchText(value);
    if (value) {
      // Filter searchResults
      const ixs = filterBag(wordBag, value);
      setSearchResults(ixs.map((i) => results[i]));
    } else {
      // Reset searchResults
      setSearchResults(results);
    }
  }

  function onClear() {
    onSearch('');
  }

  return (
    <div
      data-testid="LTPDSIDContentBodySearch"
      className="align-items-center d-flex pb-1 mb-1 pl-3 pr-4 pt-2 border-bottom"
    >
      <div className="mr-2 text-dark">
        <i className="fa fa-search" />
      </div>
      <input
        className="form-control border-0"
        placeholder={'Enter a search query for "' + name + '"'}
        value={searchText}
        onChange={(e) => {
          const value = e.target.value || '';
          onSearch(value);
        }}
      />
      {searchText && (
        <OverlayTriggerWrapper
          placement="left"
          trigger={['hover']}
          overlay={<Tooltip id="clear-table-search">Clear</Tooltip>}
        >
          <button className="p-0" onClick={() => onClear()}>
            <i className="fa fa-times" />
          </button>
        </OverlayTriggerWrapper>
      )}
    </div>
  );
}

function LTPDSIDContentBodyPager({
  pageNum,
  totalPages,
  setPageNum,
  searchResultsLength,
}) {
  if (totalPages < 2) return null;
  return (
    <div
      data-testid="LTPDSIDContentBodyPager"
      className="border-bottom mb-2 d-flex align-items-center justify-content-center pt-2 pb-2"
      css={css(`
      .pager-btn {
        color: #bbb;
        cursor: unset;
        &.visible {
          color: red;
          cursor: pointer;
        }
      }
    `)}
    >
      <button
        className={cx('pager-btn prev-btn p-0 mr-4', { visible: pageNum > 0 })}
        onClick={() => pageNum > 0 && setPageNum(Math.max(pageNum - 1, 0))}
      >
        <i className="fa fa-caret-left" />
      </button>
      <div>
        {pageNum + 1} of {totalPages}
      </div>
      <button
        className={cx('pager-btn next-btn ml-4 p-0', {
          visible: pageNum < totalPages - 1,
        })}
        onClick={() =>
          pageNum < totalPages - 1 &&
          setPageNum(Math.min(pageNum + 1, searchResultsLength - 1))
        }
      >
        <i className="fa fa-caret-right" />
      </button>
    </div>
  );
}

function LTPDSIDContentBodyNoResults({ searchText }) {
  const text = searchText
    ? `No results for "${searchText}".`
    : 'No data for this location.';
  return (
    <div
      data-testid="LTPDSIDContentBodyNoResults"
      className="d-flex justify-content-center align-items-center"
      css={css('min-height: 200px;')}
    >
      {text}
    </div>
  );
}

function DestroyDataPanelButton({ id, onClose, bbox }) {
  const [errors, setErrors] = useState([]);
  const opts = {
    variables: { input: { id } },
    refetchQueries: refetchQueries.dataPanels.destroy({ bbox }),
  };
  const [destroyPanel] = useMutation(DestroyDataPanel, opts);

  function confirmAndRemove() {
    const yes = confirm('Are you sure you want to remove this panel?');
    if (!yes) return;

    destroyPanel()
      .then((res) => {
        const succ = nested(res, 'data.destroyDataPanel.success');
        const err = nested(res, 'data.destroyDataPanel.errors');
        if (succ) {
          onClose();
        } else {
          setErrors(err);
        }
      })
      .catch(() => {
        setErrors(['Unexpected error.']);
      });
  }

  return (
    <div className="float-right">
      <button
        style={{ marginRight: 5, marginTop: -8, fontWeight: 'bold' }}
        className="btn btn-danger"
        onClick={() => confirmAndRemove()}
      >
        Remove
      </button>
      {NonEmptyArray.is(errors) && (
        <div
          id="destroy-error-list"
          style={{ float: 'left', marginRight: 10, fontSize: '0.8em' }}
        >
          {errors.map((v, i) => (
            <div key={i}>{v}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function LTPDSIDContentBodyEditForm() {
  const { locActiveId: id, locActiveModalName: name } = useContextState();
  const modalName = 'EDIT_DATA_PANEL';
  const isOpen = name === modalName;
  const cmd = useCommands();
  const variables = { id };
  const opts = { variables };
  const { data, loading, errors, error } = useCachedQuery(GetDataPanel, opts);
  const value = I.getIn(data, ['dataPanel'], {});

  const setOpen = (bool) => {
    cmd.set(['locActiveModalName'], bool ? modalName : null);
  };
  const onClose = () => setOpen(false);
  const bbox = useBbox();
  if (!isOpen) return null;

  const onRemove = () => {
    cmd.set(['locActiveModalName'], null);
    cmd.set(['locActiveId'], null);
  };

  const title = (
    <div
      css={css({
        paddingBottom: 10,
        borderBottom: 'solid 1px #efefef',
      })}
    >
      EDIT PANEL{' '}
      <DestroyDataPanelButton id={id} bbox={bbox} onClose={onClose} />
    </div>
  );

  const queriesToFetchAgain = refetchQueries.dataPanels.edit({ id, bbox });

  return (
    <Fragment>
      <ErrorsView errors={errors} error={error} />
      <Modal
        key={String(id) + '-1'}
        isOpen
        bbox={bbox}
        onClose={onClose}
        onSave={() => {}}
        onRemove={onRemove}
        value={value}
        refetchQueries={queriesToFetchAgain}
        mutation={UpdateDataPanel}
        queryName="updateDataPanel"
        isLoading={loading}
        schema={schema}
        namespace="panel"
        title={title}
      />
    </Fragment>
  );
}

function useDataPanel(id, o) {
  const variables = { id };
  const opts = { ...o, variables };
  const { data, ...rest } = useCachedQuery(GetDataPanel, opts);
  const d = I.getIn(data, ['dataPanel'], []);
  return { ...rest, data: d };
}

function LTPDSIDContentBodyDupForm() {
  const { locActiveId: id, locActiveModalName: name } = useContextState();
  const modalName = 'DUP_DATA_PANEL';
  const isOpen = name === modalName;
  const cmd = useCommands();
  const bbox = useBbox();
  const setOpen = (bool) => {
    cmd.set(['locActiveModalName'], bool ? modalName : null);
  };
  const onClose = () => setOpen(false);
  const opts = { skip: !isOpen };
  const { data, loading, errors } = useDataPanel(id, opts);

  if (!isOpen) return null;

  const onSave = () => {
    cmd.set(['locActiveModalName'], null);
    cmd.set(['locActiveId'], null);
  };

  // Remove id from the value
  const idPath = lensPath(['id']);
  const value = setPath(idPath, null, data);

  return (
    <Fragment>
      <ErrorsView errors={errors} />
      <Modal
        isOpen
        bbox={bbox}
        onClose={onClose}
        onSave={onSave}
        value={value}
        namespace="panel"
        isLoading={loading}
        mutation={CreateDataPanel}
        queryName="createDataPanel"
        refetchQueries={refetchQueries.dataPanels.new({ bbox })}
        title={<div>NEW PANEL</div>}
        schema={schema}
      />
    </Fragment>
  );
}

function convLatLonToGeom(latlon) {
  if (!latlon) return 'POINT (0 0)';
  let lat, lon;
  if (I.Map.isMap(latlon)) {
    // $FlowFixMe[prop-missing]
    const d = ((latlon.toJS(): any): { lat: number, lon: number });
    lat = d.lat;
    lon = d.lon;
  } else {
    lat = latlon.lat;
    lon = latlon.lon;
  }
  return `POINT(${lon} ${lat})`;
}

function getMap() {
  return window.cityMap || window.districtMap;
}

function getMapZoom() {
  const map = getMap();
  if (!map) return 14;
  return map.getZoom();
}

function useDataPanelData() {
  const s = useContextState();
  const id = s.locActiveId;
  const theGeom = convLatLonToGeom(s.focusedLatlon);
  const zoom = getMapZoom();
  const variables = { theGeom, id, zoom };
  const opts = { variables };
  const { data, ...rest } = useCachedQuery(GetDataPanelData, opts);
  type ResultT = { [key: string]: any };
  type LabelsT = { [key: string]: string };
  type ResultsT = {
    results: ResultT[],
    labels: LabelsT,
    fields: string[],
    errors?: string[],
    backtrace?: string,
  };

  const keyPath = ['dataPanel', 'data'];
  const empty = { results: [], labels: {}, fields: [] };
  const dataPanelData: ResultsT = I.getIn(data, keyPath, empty) || empty;
  return { ...rest, data: dataPanelData };
}

function LTPDSIDContentBody() {
  const { data, loading } = useDataPanelData();
  const { results = [], labels } = data;
  const [pageNum, setPageNum] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState(results);
  const currentPage = searchResults[pageNum];
  const totalPages = results.length;
  // Reset the page number when search results change.
  useEffect(() => setPageNum(0), [searchResults]);
  useEffect(() => {
    if (same(results, searchResults)) return;
    setSearchResults(results);
  }, [results]);

  if (loading) return <LTPDSIDContentBodySkeleton />;
  return (
    <div
      data-testid="LTPDSIDContentBody"
      css={css(`
      color: black;
     `)}
    >
      <LTPDSIDContentBodySearch
        results={searchResults}
        searchText={searchText}
        setSearchResults={setSearchResults}
        setSearchText={setSearchText}
      />
      <LTPDSIDContentBodyPager
        searchResultsLength={searchResults.length}
        pageNum={pageNum}
        setPageNum={setPageNum}
        totalPages={totalPages}
      />
      {searchResults.length === 0 && (
        <LTPDSIDContentBodyNoResults searchText={searchText} />
      )}
      <LTPDSIDContentBodyDupForm />
      <LTPDSIDContentBodyEditForm />
      {currentPage &&
        Object.keys(currentPage).map((key) => (
          <LTPDSIDContentBodyRow
            label={labels[key]}
            value={currentPage[key]}
            key={key}
          />
        ))}
    </div>
  );
}

function LTPDSIDContentFooterTagsButton() {
  const { data: panel } = useCurrentDataPanel();
  const tags = panel ? panel.tags : [];

  return (
    <OverlayTriggerWrapper
      placement="right"
      trigger={['click']}
      rootClose
      overlay={
        <Popover id={'field-select-btn-popover'}>
          <ul className="list-inline" style={{ margin: 0 }}>
            {tags.map((k) => (
              <li key={k} className="tag-link" style={{ color: 'blue' }}>
                {k}
              </li>
            ))}
          </ul>
        </Popover>
      }
    >
      <div className="cursor-pointer">
        <i className="fa fa-tags" /> Tags
      </div>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentFooterSourceButton() {
  const { data: panel } = useCurrentDataPanel();
  const source = panel ? panel.source : '';

  return (
    <OverlayTriggerWrapper
      placement="right"
      trigger={['click']}
      rootClose
      overlay={
        <Popover id={'field-select-btn-popover'}>
          <span
            dangerouslySetInnerHTML={{ __html: source || 'NYC Government.' }}
          />{' '}
          <small className="text-muted">
            <b>via</b> Ginkgo
          </small>
        </Popover>
      }
    >
      <div className="cursor-pointer">
        <i className="fa fa-info-circle" /> Source
      </div>
    </OverlayTriggerWrapper>
  );
}

function LTPDSIDContentFooter() {
  return (
    <footer
      data-testid="LTPDSIDContentFooter"
      className="d-flex align-items-center pl-3 pr-3 pt-3 pb-3 border-top border-bottom"
      css={css('color: #999; font-size: 1.2rem; ')}
    >
      <div className="mr-4">
        <LTPDSIDContentFooterSourceButton />
      </div>
      <LTPDSIDContentFooterTagsButton />
    </footer>
  );
}

function LTPDSIDContentNav() {
  const onClear = useClearDataPanel();

  return (
    <div data-testid="LTPDSIDContentNav">
      <button
        className="mb-2 mt-2"
        css={css('color: var(--green);')}
        onClick={onClear}
      >
        <i className="fa fa-long-arrow-left" /> Back
      </button>
    </div>
  );
}

function LTPDSIDContent() {
  return (
    <div data-testid="LTPDSIDContent">
      <LTPDSIDContentNav />
      <LTPDSIDContentHeader />
      <LTPDSIDContentBody />
      <LTPDSIDContentFooter />
    </div>
  );
}

function LTPublicDataBody() {
  const { locActiveId } = useContextState();
  const isSearching = Boolean(locActiveId) === false;
  const BodyComponent = isSearching
    ? LTPublicDataSearchResults
    : LTPublicDataSearchItemDetail;

  return (
    <div data-testid="LTPublicDataBody" className="">
      <LTPublicDataSearchInput />
      <BodyComponent />
    </div>
  );
}

// Abbrev: LTPDSID
function LTPublicDataSearchItemDetail() {
  const { locActiveId: id } = useContextState();
  const Body = id ? LTPDSIDContent : LTPDSIDEmpty;
  return <Body />;
}

function LTLocationAdminHeaderNewDataPanelForm({ isOpen, onClose }) {
  const bbox = useBbox();

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      bbox={bbox}
      namespace="panel"
      mutation={CreateDataPanel}
      queryName="createDataPanel"
      refetchQueries={refetchQueries.dataPanels.new({ bbox })}
      title={<div>NEW PANEL</div>}
      schema={schema}
    />
  );
}

function LTLocationAdminHeaderNewDataPanelButton() {
  const cmd = useCommands();
  const setOpen = (bool) => {
    cmd.set(['locActiveModalName'], bool ? 'NEW_DATA_PANEL' : null);
  };
  const { locActiveModalName } = useContextState();
  const isOpen = locActiveModalName === 'NEW_DATA_PANEL';

  return (
    <Fragment>
      <OverlayTriggerWrapper
        placement="left"
        trigger={['hover']}
        overlay={
          <Tooltip id="new-data-panel-tooltip">Add a Data Panel</Tooltip>
        }
      >
        <button className="text-info pt-3" onClick={() => setOpen(true)}>
          New <i className="fa fa-plus" />
        </button>
      </OverlayTriggerWrapper>
      <LTLocationAdminHeaderNewDataPanelForm
        isOpen={isOpen}
        onClose={() => setOpen(false)}
      />
    </Fragment>
  );
}

function LTLocationAdminHeader() {
  if (!isAdmin()) return null;
  return (
    <div data-testid="LTLocationAdminHeader" className="small">
      <LTLocationAdminHeaderNewDataPanelButton />
    </div>
  );
}

export default function LTPublicData(): null | Element<string> {
  let dataPanels = useQueryParams().getIn(['load', 'dataPanels']);
  if (isEmbedded() && !dataPanels) return null;

  return (
    <div
      data-testid="LTPublicData"
      css={css(`
        background-color: white;
        min-height: 100%;
      `)}
    >
      <div
        data-testid="LTPublicDataHeader"
        className="d-flex justify-content-between pr-3 pl-3"
      >
        <h5
          className="text-uppercase m-0 font-weight-bold mb-0 pt-3"
          css={css('color: #333;')}
        >
          Public Datasets
        </h5>
        <LTLocationAdminHeader />
      </div>
      <LTPublicDataBody />
    </div>
  );
}
